import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Popover, Button, List, Typography, Tag, Empty, message } from 'antd';
import { BellOutlined, PhoneOutlined, MessageOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import api from '../config/api';
import dayjs from 'dayjs';
import { notification } from 'antd';
import { useRealtimeTicket } from '../contexts/RealtimeTicketContext';
import { usePermission } from '../hooks/usePermission';

interface NotificationBellProps {
  user: { id: string; role?: string } | null;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ user }) => {
  const navigate = useNavigate();
  const canCallMeQueue = usePermission('call_me_queue');
  const prevCountRef = useRef<number | null>(null);
  const bellAnimTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isBellAnimating, setIsBellAnimating] = useState(false);
  const [liveCallMeCount, setLiveCallMeCount] = useState<number>(0);
  const [claimingCallMeId, setClaimingCallMeId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { on } = useRealtimeTicket();

  const isAgentOrSales = [
    'admin',
    'agent',
    'developer',
    'sales',
    'sales_agent',
    'sales_rep_internal',
    'agent_partner',
    'sales_rep_associated',
    'tv_dashboard',
  ].includes(user?.role || '');

  const triggerBellAnimation = () => {
    setIsBellAnimating(true);
    if (bellAnimTimerRef.current) clearTimeout(bellAnimTimerRef.current);
    bellAnimTimerRef.current = setTimeout(() => setIsBellAnimating(false), 1800);
  };

  const { data: callMeBellSoundSetting } = useQuery(
    'call-me-bell-sound-setting',
    () => api.get('/api/settings/call-me-bell-sound').then((r) => r.data),
    { enabled: !!user && !!isAgentOrSales && canCallMeQueue, retry: false, staleTime: 60000, refetchInterval: 120000 }
  );
  const isCallMeSoundEnabled = callMeBellSoundSetting?.enabled === true;

  const playCallMeSound = useCallback(() => {
    if (!isCallMeSoundEnabled) return;
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 900;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.25, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.25);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.25);
    } catch {
      // Browser may block autoplayed sounds until user interaction.
    }
  }, [isCallMeSoundEnabled]);

  // Push notification when ticket is created from chat
  useEffect(() => {
    if (!user || !isAgentOrSales) return;
    const unsub = on('ticket:created', (ticket: any) => {
      if (ticket?.source_channel_id) {
        notification.info({
          message: 'New Ticket From Chat',
          description: `Ticket #${ticket.ticket_number || ticket.id} was created from chat.`,
          placement: 'topRight',
          duration: 5,
          icon: <MessageOutlined style={{ color: '#5856d6' }} />,
          onClick: () => navigate('/tickets'),
        });
      }
    });
    return unsub;
  }, [user, isAgentOrSales, on, navigate]);

  // Realtime Call Me notification (instant push and cache refresh)
  useEffect(() => {
    if (!user || !isAgentOrSales || !canCallMeQueue) return;
    const unsub = on('callme:created', (item: any) => {
      queryClient.invalidateQueries('call-me-count');
      queryClient.invalidateQueries('call-me-queue');
      notification.warning({
        message: 'New Call Me Request',
        description: `${item?.business_name || 'Store'} (${item?.store_id || 'N/A'}) needs assistance.`,
        placement: 'topRight',
        duration: 5,
        icon: <PhoneOutlined style={{ color: '#faad14' }} />,
        onClick: () => navigate('/tickets?tab=callme'),
      });
      triggerBellAnimation();
      playCallMeSound();
    });
    return unsub;
  }, [user, isAgentOrSales, canCallMeQueue, on, queryClient, navigate, playCallMeSound]);

  const { data: callMeCountData } = useQuery(
    'call-me-count',
    () => api.get('/api/call-me/count').then((r) => r.data),
    { enabled: !!user && !!isAgentOrSales && canCallMeQueue, retry: false, refetchInterval: 10000, refetchIntervalInBackground: true }
  );

  useEffect(() => {
    if (!user || !isAgentOrSales || !canCallMeQueue) {
      setLiveCallMeCount(0);
      return;
    }

    let alive = true;
    const poll = async () => {
      try {
        const res = await api.get('/api/call-me/count', { params: { _ts: Date.now() } });
        if (alive) setLiveCallMeCount(Number(res.data?.count ?? 0));
      } catch {
        // keep last known count on transient errors
      }
    };

    poll();
    const id = setInterval(poll, 5000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [user, isAgentOrSales, canCallMeQueue]);

  const { data: callMeQueueData } = useQuery(
    'call-me-queue',
    () => api.get('/api/call-me').then((r) => r.data),
    { enabled: !!user && !!isAgentOrSales && canCallMeQueue, retry: false, refetchInterval: 15000, refetchIntervalInBackground: true }
  );

  const count = liveCallMeCount ?? callMeCountData?.count ?? 0;
  const items = callMeQueueData?.items ?? [];

  // Popup notification when new Call Me arrives (count increases)
  useEffect(() => {
    if (!user || !isAgentOrSales || !canCallMeQueue) return;
    const prev = prevCountRef.current;
    prevCountRef.current = count;
    if (prev !== null && count > prev) {
      notification.warning({
        message: 'New Call Me Request',
        description: `${count - prev} new Call Me request(s) in queue. Store needs assistance.`,
        placement: 'topRight',
        duration: 5,
        icon: <PhoneOutlined style={{ color: '#faad14' }} />,
        onClick: () => navigate('/tickets?tab=callme'),
      });
      triggerBellAnimation();
      playCallMeSound();
    }
  }, [count, user, isAgentOrSales, canCallMeQueue, navigate, playCallMeSound]);

  useEffect(() => {
    return () => {
      if (bellAnimTimerRef.current) clearTimeout(bellAnimTimerRef.current);
    };
  }, []);

  if (!user || !isAgentOrSales) return null;
  if (!canCallMeQueue) return null;

  const handleConvertCallMe = async (itemId: string) => {
    setClaimingCallMeId(itemId);
    try {
      await api.post(`/api/call-me/${itemId}/claim`);
      queryClient.invalidateQueries('call-me-count');
      queryClient.invalidateQueries('call-me-queue');
      navigate(`/workspace/new?fromCallMe=${itemId}`);
    } catch (e: any) {
      const code = e.response?.data?.code;
      const msg = e.response?.data?.error || 'Could not start conversion.';
      if (code === 'CALL_ME_ALREADY_CLAIMED') {
        message.warning(msg);
      } else {
        message.error(msg);
      }
      queryClient.invalidateQueries('call-me-count');
      queryClient.invalidateQueries('call-me-queue');
    } finally {
      setClaimingCallMeId(null);
    }
  };

  const content = (
    <div style={{ width: 360, maxHeight: 400, overflow: 'auto' }}>
      <div style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0', marginBottom: 8 }}>
        <Typography.Text strong>Call Me Queue</Typography.Text>
        {count > 0 && (
          <Badge count={count} style={{ marginLeft: 8, backgroundColor: '#faad14' }} />
        )}
      </div>
      {items.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No pending requests" style={{ padding: 24 }} />
      ) : (
        <List
          size="small"
          dataSource={items}
          renderItem={(item: any) => {
            const isOwnConverting =
              item.status === 'converting' &&
              !!user?.id &&
              String(item.claimed_by) === String(user.id);
            return (
              <List.Item
                key={item.id}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/tickets?tab=callme`)}
                actions={[
                  <Button
                    type="link"
                    size="small"
                    loading={claimingCallMeId === item.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleConvertCallMe(item.id);
                    }}
                  >
                    {isOwnConverting ? 'Resume' : 'Convert'}
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={<span>{item.business_name || 'Store'} · {item.store_id}</span>}
                  description={
                    <span>
                      <Tag>{item.service_type || '—'}</Tag>
                      {isOwnConverting && (
                        <Tag color="processing" style={{ marginRight: 4 }}>
                          Converting (you)
                        </Tag>
                      )}
                      {dayjs(item.created_at).format('MMM D, h:mm A')}
                    </span>
                  }
                />
              </List.Item>
            );
          }}
        />
      )}
      <div style={{ paddingTop: 8, borderTop: '1px solid #f0f0f0' }}>
        <Button type="link" size="small" block onClick={() => navigate('/tickets?tab=callme')}>
          View all in Tickets
        </Button>
      </div>
    </div>
  );

  return (
    <Popover content={content} title={null} trigger="click" placement="bottomRight">
      <Badge count={count} size="small" offset={[-2, 2]} style={{ backgroundColor: count > 0 ? '#faad14' : undefined }}>
        <Button
          type="default"
          icon={<BellOutlined />}
          style={{
            display: 'flex',
            alignItems: 'center',
            animation: isBellAnimating ? 'callMeBellRing 0.6s ease-in-out 3' : undefined,
          }}
        />
      </Badge>
      <style>
        {`@keyframes callMeBellRing { 0% { transform: rotate(0deg); } 15% { transform: rotate(12deg); } 30% { transform: rotate(-10deg); } 45% { transform: rotate(8deg); } 60% { transform: rotate(-6deg); } 75% { transform: rotate(4deg); } 100% { transform: rotate(0deg); } }`}
      </style>
    </Popover>
  );
};

export default NotificationBell;
